import React, { useState } from 'react';
import { Copy, Download, FileSpreadsheet, FileText, Folder, Loader2, Package, RefreshCw, SquareCheck, Trash2 } from 'lucide-react';
import { IndieGeneratedFilesTab, downloadGeneratedFile, isBuilding, useLiveGeneratedFile } from '../IndieGeneratedFiles';

const FILE_FORMAT_LABEL: Record<string, string> = { xlsx: 'Excel', pdf: 'PDF', pptx: 'Slides', csv: 'CSV' };

/** Format and live build status of a file-backed artifact. */
const ArtifactFileStatus: React.FC<{ file: NonNullable<OrbitArtifact['file']>; clientId?: number | null }> = ({ file, clientId }) => {
  const live = useLiveGeneratedFile(file, clientId) ?? file;
  const format = FILE_FORMAT_LABEL[live.file_format] ?? live.file_format.toUpperCase();
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] ${live.status === 'error' ? 'text-[#B42318]' : 'text-[#6941C6]'}`}>
      {isBuilding(live.status) && <Loader2 className="h-3 w-3 animate-spin" />}
      {format} · {isBuilding(live.status) ? 'Building…' : live.status === 'error' ? (live.error_message || 'Build failed') : 'Ready'}
    </span>
  );
};
import type { ActionCard, RightTab } from '../indie-chat.types';
import type { OrbitArtifact } from '../../../hooks/useOrbitArtifacts';

interface Props {
  activeTab: RightTab;
  onTabChange: (tab: RightTab) => void;
  actionCards?: ActionCard[];
  weekLabel?: string;
  onActionSelect?: (text: string) => void;
  onRefresh?: () => Promise<void>;
  refreshing?: boolean;
  artifacts?: OrbitArtifact[];
  artifactsLoading?: boolean;
  onArtifactUpdate?: (artifactId: number, input: { title?: string; content_markdown?: string; artifact_type?: string }) => Promise<OrbitArtifact | undefined>;
  onArtifactDownload?: (artifact: OrbitArtifact) => void;
  onArtifactDelete?: (artifact: OrbitArtifact) => void;
  clientId?: number;
  conversationId?: string;
}

const badgeStyle: Record<string, { background: string; color: string }> = {
  amber: { background: '#FFFAEB', color: '#B54708' },
  red: { background: '#FEF3F2', color: '#B42318' },
  purple: { background: '#F9F5FF', color: '#6941C6' },
  green: { background: '#ECFDF3', color: '#079455' },
};

const V3ChatRightSidebar: React.FC<Props> = ({
  activeTab, onTabChange, actionCards = [], weekLabel = 'This week', onActionSelect,
  onRefresh, refreshing, artifacts = [], artifactsLoading, onArtifactDownload, onArtifactDelete,
  clientId, conversationId,
}) => {
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const copyArtifact = async (artifact: OrbitArtifact) => {
    try {
      await navigator.clipboard.writeText(artifact.content_markdown || artifact.preview_text || '');
      setCopiedId(artifact.id);
      window.setTimeout(() => setCopiedId(null), 1600);
    } catch {
      setCopiedId(null);
    }
  };

  return (
    <aside className="indie-chat flex h-full w-[220px] shrink-0 flex-col overflow-hidden border-l border-[#EAECF0] bg-white" aria-label="Chat actions and artifacts">
      <div className="flex h-[37px] shrink-0 items-center overflow-x-auto border-b border-[#EAECF0] px-2.5 [scrollbar-width:none]">
        <button
          type="button"
          onClick={() => onTabChange('actions')}
          className={`flex items-center gap-1 border-b-2 px-2 py-2 text-xs ${activeTab === 'actions'
              ? 'border-[#101828] font-medium text-[#101828]'
              : 'border-transparent text-[#98A2B3]'
            }`}
        >
          <SquareCheck className="h-3 w-3" />
          Actions
        </button>
        <button
          type="button"
          onClick={() => onTabChange('artifacts')}
          className={`flex items-center gap-1 border-b-2 px-2 py-2 text-xs ${activeTab === 'artifacts'
              ? 'border-[#101828] font-medium text-[#101828]'
              : 'border-transparent text-[#98A2B3]'
            }`}
        >
          <Package className="h-3 w-3" />
          Artifacts
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#D0D5DD] text-[9px] font-semibold text-white">
            {artifacts.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onTabChange('files')}
          className={`flex items-center gap-1 border-b-2 px-2 py-2 text-xs ${activeTab === 'files'
              ? 'border-[#101828] font-medium text-[#101828]'
              : 'border-transparent text-[#98A2B3]'
            }`}
        >
          <FileSpreadsheet className="h-3 w-3" />
          Files
        </button>
      </div>
      {activeTab === 'files' ? (
        <div className="flex-1 overflow-y-auto px-3 pt-3 pb-3">
          <IndieGeneratedFilesTab clientId={clientId} conversationId={conversationId} />
        </div>
      ) : activeTab === 'actions' ? (
        <div className="flex flex-1 flex-col items-center justify-start px-4 pt-16 text-center">
          <h3 className="text-[14px] font-semibold leading-[20px] text-[#667085] text-center">
            Action Items - Coming soon
          </h3>
          <p className="mt-1 text-[12px] font-normal leading-[16px] tracking-[0.12px] text-[#667085] text-center">
            You'll be notified when the feature is ready!
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="px-3.5 pb-1.5 pt-3 text-[10px] font-medium uppercase tracking-[.6px] text-[#98A2B3]">
            Saved by Indie <span className="normal-case">· {artifacts.length} items</span>
          </div>
          <div className="flex-1 overflow-y-auto px-3.5 pb-3">
            {artifactsLoading ? (
              <Loader2 className="mx-auto my-8 h-4 w-4 animate-spin text-[#98A2B3]" />
            ) : artifacts.length === 0 ? (
              <div className="py-8 text-center">
                <FileText className="mx-auto mb-2 h-5 w-5 text-[#D0D5DD]" />
                <p className="text-[11px] font-medium text-[#98A2B3]">No saved artifacts yet</p>
              </div>
            ) : (
              artifacts.map((artifact) => (
                <div
                  key={artifact.id}
                  className="mb-2 flex flex-col gap-2 rounded-[10px] border border-[#EAECF0] bg-white p-3"
                >
                  <div className="flex items-start gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[5px] border border-[#EAECF0] bg-[#F9FAFB]">
                      <Folder className="h-3 w-3 text-[#667085]" />
                    </span>
                    <span className="min-w-0 flex-1 text-xs font-medium leading-[135%] text-[#101828]">
                      {artifact.title}
                    </span>
                  </div>
                  {artifact.file && <ArtifactFileStatus file={artifact.file} clientId={artifact.file.client_id ?? clientId} />}
                  <p className="line-clamp-3 text-[11px] leading-[150%] text-[#667085]">
                    {artifact.preview_text || artifact.content_markdown}
                  </p>
                  <div className="flex gap-[5px]">
                    <button
                      type="button"
                      onClick={() =>
                        onActionSelect?.(artifact.content_markdown || artifact.preview_text || artifact.title)
                      }
                      className="flex flex-1 items-center justify-center rounded-md border border-[#C3BFFA] bg-[#EEEDFE] px-2.5 py-1 text-[11px] font-medium text-[#534AB7]"
                    >
                      Open in chat
                    </button>
                    <button
                      type="button"
                      onClick={() => void copyArtifact(artifact)}
                      aria-label="Copy artifact"
                      className="rounded-md border border-[#EAECF0] px-2 text-[#667085]"
                    >
                      <Copy className="h-3 w-3" />
                      {copiedId === artifact.id && <span className="sr-only">Copied</span>}
                    </button>
                    <button
                      type="button"
                      onClick={() => (artifact.file
                        ? void downloadGeneratedFile(artifact.file.client_id ?? clientId, artifact.file).catch(() => window.alert('Unable to download this file right now.'))
                        : onArtifactDownload?.(artifact))}
                      aria-label="Download artifact"
                      className="rounded-md border border-[#EAECF0] px-2 text-[#667085]"
                    >
                      <Download className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onArtifactDelete?.(artifact)}
                      aria-label="Delete artifact"
                      className="rounded-md border border-[#EAECF0] px-2 text-[#D92D20]"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </aside>
  );
};

export default V3ChatRightSidebar;